import { useCallback } from 'react';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../../query';
import { RequestMeta } from '../../../temporary/requestmeta.types';
import { useStudentApi } from '../../api';
import { studentKeys } from '../../query-key-factory';
import {
  ConsumerFeesSearch,
  ConsumerStudentFeeResponse,
  ConsumerStudentsFeeResponse,
} from './fees.types';

type GetFeeContext = {
  meta?: {
    feeId?: string;
  };
};

type GetFeesContext = {
  meta?: {
    queryString?: string;
  };
};

const useGetConsumerFee = () => {
  const StudentApi = useStudentApi();

  const getFee = async (context: GetFeeContext) => {
    const { feeId } = context.meta || {};
    const { data } = await StudentApi.get<ConsumerStudentFeeResponse>(`/consumer/fees/${feeId}`);
    return data;
  };

  return getFee;
};

const useGetConsumerFees = () => {
  const StudentApi = useStudentApi();

  const getFees = async (context: GetFeesContext) => {
    const { queryString } = context.meta || {};
    const { data } = await StudentApi.get<ConsumerStudentsFeeResponse>(
      `/consumer/fees?${queryString}`
    );
    return data;
  };

  return getFees;
};

/***
 * Gets a fee by id
 */
const useShowConsumerFee = ({ feeId }: { feeId: string }) => {
  const getFee = useGetConsumerFee();

  return useQuery({
    queryKey: studentKeys.id('user').consumer.fees.id(feeId).detail,
    queryFn: getFee,

    meta: { feeId },
  });
};

interface UseListConsumerFeesArgs {
  enabled?: boolean;
  queryOptions?: ConsumerFeesSearch;
  pagination?: QueryPagination;
}

/**
 * Gets a list of fees for the current user
 */
const useListConsumerFees = ({
  enabled = true,
  queryOptions,
  pagination,
}: UseListConsumerFeesArgs) => {
  const getFees = useGetConsumerFees();
  const { queryString, queryObject, queryPagination } = createQueryOptions<ConsumerFeesSearch>({
    search: queryOptions,
    pagination,
  });

  return useQuery({
    queryKey: [...studentKeys.id('user').consumer.fees.all, queryObject, queryPagination],
    queryFn: getFees,
    enabled,
    meta: { queryString },
  });
};

const useInfiniteListConsumerFees = ({
  enabled = true,
  queryOptions,
}: Omit<UseListConsumerFeesArgs, 'pagination'>) => {
  const getFees = useGetConsumerFees();
  const { queryObject, queryString: existingQueryString } = createQueryOptions<ConsumerFeesSearch>({
    search: queryOptions,
  });
  const getInfiniteData = useCallback(
    async ({ pageParam = { limit: 24, offset: 0 } }) => {
      // Create a new query string with the existing query string and the new pagination
      const { queryString } = createQueryOptions({
        pagination: {
          limit: pageParam.limit,
          offset: pageParam.offset,
        },
        existingSearchString: existingQueryString,
      });
      const result = await getFees({
        meta: { queryString },
      });
      return result;
    },
    [existingQueryString, getFees]
  );

  const queryKey = studentKeys.id('user').consumer.fees.infinite;

  return useInfiniteQuery({
    queryKey: [...queryKey, queryObject],
    queryFn: getInfiniteData,
    enabled: enabled,
    initialPageParam: { limit: 24, offset: 0 },
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      const { meta } = lastPage as { meta: RequestMeta };
      if (!meta) return undefined;
      const { has_more, limit, offset } = meta;
      if (!has_more) {
        return undefined;
      }
      return { limit, offset: offset + limit };
    },
  });
};

export { useInfiniteListConsumerFees, useListConsumerFees, useShowConsumerFee };
